/**
 * TopChrome — Workspace-V2 top bar (44px, 3-zone).
 *
 * Sprint-2 Task 2.1 (sprint.md:231). Zones:
 *   left   — Echelon wordmark + breadcrumb
 *   center — ModesRow + RegionPill + live-rate indicator
 *   right  — OPEN GLOBE button
 *
 * Design: inline-styled per sprint-1 convention; --cd-* tokens only.
 * Port of `workspace/chrome.jsx` TopBar structure adapted to 44px
 * height per master plan line 940.
 *
 * Consumers: session-2 InvestigationWorkspacePage V2-gated shell.
 */

import { ModesRow } from '../workspace/ModesRow';
import type { WorkspaceModeId } from '../workspace/modeRegistry';

export type RegionId = 'global' | 'europe' | 'asia' | 'americas' | 'mena';

export interface RegionDef {
  readonly id: RegionId;
  readonly label: string;
}

export const REGIONS: readonly RegionDef[] = [
  { id: 'global', label: 'GLOBAL' },
  { id: 'europe', label: 'EU' },
  { id: 'asia', label: 'ASIA' },
  { id: 'americas', label: 'AM' },
  { id: 'mena', label: 'MENA' },
];

/**
 * Live collection-rate readout state (FR-W-2, cycle-105.8).
 *  - `hidden`  — health query errored or not yet resolved → render nothing (NFR-4: no fake data)
 *  - `sources` — health ok, no derivable rate → "M/N sources live" only
 *  - `full`    — health ok + a derived collection rate → "K/min collected · M/N sources live"
 * `isFloor` marks a trailing window saturated by the fetch limit (true rate is ≥ the shown value).
 */
export type LiveRateReadout =
  | { kind: 'hidden' }
  | { kind: 'sources'; online: number; total: number }
  | { kind: 'full'; online: number; total: number; perMin: number; isFloor: boolean };

export interface TopChromeProps {
  activeMode: WorkspaceModeId;
  onModeChange: (mode: WorkspaceModeId) => void;
  activeRegion: RegionId;
  onRegionChange: (region: RegionId) => void;
  /** Live collection-rate readout (FR-W-2). Derived in the page; rendered in the SCOPE band. */
  liveRate: LiveRateReadout;
  /** Active-investigation pip (FR-W-3). Null/undefined when no investigation is tracked. */
  activeInvestigation?: { code: string; title: string } | null;
  /** Clears the tracked investigation (pip × button). */
  onClearInvestigation?: () => void;
  onOpenGlobe: () => void;
  /** Whether globe takeover is currently active — drives button state. */
  globeOpen?: boolean;
  /** Id of the canvas element `ModesRow` controls — for aria-controls. */
  canvasId?: string;
  className?: string;
}

export function TopChrome({
  activeMode,
  onModeChange,
  activeRegion,
  onRegionChange,
  liveRate,
  activeInvestigation,
  onClearInvestigation,
  onOpenGlobe,
  globeOpen = false,
  canvasId,
  className,
}: TopChromeProps) {
  return (
    <div
      className={className}
      style={{
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
        position: 'relative',
        zIndex: 2,
      }}
    >
      {/* ── Row 1: 44px header — wordmark · modes/region · globe ── */}
      <header
        role="banner"
        style={{
          display: 'flex',
          alignItems: 'stretch',
          height: 44,
          borderBottom: '1px solid var(--cd-border-strong)',
          background: 'var(--cd-bg-panel)',
          boxShadow: '0 1px 0 rgba(0,0,0,0.35)',
          fontFamily: 'var(--cd-font-sans)',
        }}
      >
        {/* Left zone — wordmark */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '0 18px',
            borderRight: '1px solid var(--cd-seam)',
            background: 'var(--cd-bg-card-header)',
          }}
        >
          <span
            style={{
              fontFamily: 'var(--cd-font-mono)',
              fontSize: 10.5,
              color: 'var(--cd-fg)',
              letterSpacing: '0.18em',
              fontWeight: 500,
              textTransform: 'uppercase',
            }}
          >
            Workspace
          </span>
        </div>

        {/* Center zone — modes + region.
            minWidth:0 + overflow:hidden keep the tab row inside this
            flex slot so INVESTIGATIONS cannot paint over Open Globe
            (Playwright 1280 Desktop Chrome and 1440 e2e viewports). */}
        <nav
          aria-label="Workspace toolbar"
          data-testid="workspace-toolbar"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 16,
            padding: '0 16px',
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
          }}
        >
          <ModesRow
            activeMode={activeMode}
            onModeChange={onModeChange}
            canvasId={canvasId}
          />

          <div
            aria-hidden="true"
            style={{
              width: 1,
              height: 20,
              background: 'var(--cd-seam)',
            }}
          />

          <RegionPill activeRegion={activeRegion} onRegionChange={onRegionChange} />

          <div style={{ flex: 1 }} />

          {/* FR-W-3 (cycle-105.8): active-investigation pip. */}
          {activeInvestigation && (
            <InvestigationPip
              investigation={activeInvestigation}
              onClear={onClearInvestigation}
            />
          )}
        </nav>

        {/* Right zone — OPEN GLOBE. Reserved hit target: do not shrink,
            isolate stacking so toolbar overflow cannot intercept clicks. */}
        <div
          data-testid="open-globe-zone"
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '0 14px',
            borderLeft: '1px solid var(--cd-seam)',
            flexShrink: 0,
            position: 'relative',
            zIndex: 3,
            isolation: 'isolate',
            background: 'var(--cd-bg-panel)',
          }}
        >
          <button
            type="button"
            data-testid="open-globe"
            onClick={onOpenGlobe}
            aria-pressed={globeOpen}
            style={{
              fontFamily: 'var(--cd-font-mono)',
              fontSize: 10.5,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              fontWeight: 500,
              color: globeOpen ? 'var(--cd-sig-iris)' : 'var(--cd-fg)',
              background: globeOpen ? 'var(--cd-bg-hover)' : 'transparent',
              border: `1px solid ${globeOpen ? 'var(--cd-sig-iris)' : 'var(--cd-border-strong)'}`,
              borderRadius: 'var(--cd-radius)',
              padding: '6px 12px',
              cursor: 'pointer',
              transition: 'color .12s, background .12s, border-color .12s',
            }}
          >
            {globeOpen ? 'Close Globe' : 'Open Globe'}
          </button>
        </div>
      </header>

      {/* ── Row 2: SCOPE band (36px) — label · live collection-rate readout ──
          FR-W-2 (cycle-105.8). The live readout lives here per the canonical
          design (workspace/chrome.jsx Row 2), right-aligned. */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          padding: '0 16px',
          height: 36,
          background: 'var(--cd-bg-inset)',
          borderBottom: '1px solid var(--cd-seam)',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <span
          style={{
            fontFamily: 'var(--cd-font-mono)',
            fontSize: 9,
            color: 'var(--cd-fg-muted)',
            letterSpacing: '0.16em',
            fontWeight: 500,
          }}
        >
          SCOPE
        </span>

        <LiveRate readout={liveRate} />
      </div>
    </div>
  );
}

// ── Region pill ─────────────────────────────────────────────────────────

interface RegionPillProps {
  activeRegion: RegionId;
  onRegionChange: (region: RegionId) => void;
}

function RegionPill({ activeRegion, onRegionChange }: RegionPillProps) {
  return (
    <div
      role="radiogroup"
      aria-label="Active region"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        borderRadius: 'var(--cd-radius-lg)',
        border: '1px solid var(--cd-border)',
        overflow: 'hidden',
      }}
    >
      {REGIONS.map((region) => {
        const active = region.id === activeRegion;
        return (
          <button
            key={region.id}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onRegionChange(region.id)}
            style={{
              fontFamily: 'var(--cd-font-mono)',
              fontSize: 10,
              letterSpacing: '0.10em',
              textTransform: 'uppercase',
              fontWeight: 500,
              color: active ? 'var(--cd-fg)' : 'var(--cd-fg-muted)',
              background: active ? 'var(--cd-bg-hover)' : 'transparent',
              border: 'none',
              padding: '4px 10px',
              cursor: 'pointer',
              transition: 'color .12s, background .12s',
            }}
          >
            {region.label}
          </button>
        );
      })}
    </div>
  );
}

// ── Active-investigation pip (FR-W-3) ───────────────────────────────────

interface InvestigationPipProps {
  investigation: { code: string; title: string };
  onClear?: () => void;
}

/**
 * Dismissible pip echoing the tracked investigation across chrome:
 * `◆ INV-XXXX · <title> ×`. Clicking it (or ESC, handled by the page) clears it.
 */
function InvestigationPip({ investigation, onClear }: InvestigationPipProps) {
  const accent = 'var(--cd-sig-iris)';
  const title =
    investigation.title.length > 38 ? `${investigation.title.slice(0, 37)}…` : investigation.title;
  return (
    <button
      type="button"
      onClick={onClear}
      title="Close investigation readout (ESC)"
      aria-label={`Active investigation ${investigation.code}: ${investigation.title}. Activate to dismiss.`}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        height: 26,
        padding: '0 10px',
        fontFamily: 'var(--cd-font-mono)',
        fontSize: 10,
        letterSpacing: '0.08em',
        color: 'var(--cd-fg)',
        background: 'var(--cd-bg-hover)',
        border: `1px solid ${accent}`,
        borderLeft: `3px solid ${accent}`,
        borderRadius: 'var(--cd-radius)',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
      }}
    >
      <span aria-hidden="true" style={{ color: accent, fontSize: 9, lineHeight: 1 }}>
        ◆
      </span>
      <span style={{ color: accent, letterSpacing: '0.12em', fontWeight: 500 }}>
        {investigation.code}
      </span>
      <span aria-hidden="true" style={{ color: 'var(--cd-fg-dim)' }}>
        ·
      </span>
      <span
        style={{
          color: 'var(--cd-fg-secondary)',
          letterSpacing: '0.02em',
          textTransform: 'none',
          fontWeight: 400,
        }}
      >
        {title}
      </span>
      <span
        aria-hidden="true"
        style={{ color: 'var(--cd-fg-dim)', fontSize: 13, marginLeft: 2, lineHeight: 1 }}
      >
        ×
      </span>
    </button>
  );
}

// ── Live collection-rate indicator (FR-W-2) ─────────────────────────────

interface LiveRateProps {
  readout: LiveRateReadout;
}

/**
 * Tooltip/aria framing per Hermes review: the rate measures Echelon *feed
 * ingestion*, not external event frequency — so the label says "collected"
 * and this copy spells out the referent.
 */
const LIVE_RATE_TITLE =
  'Signals collected into Echelon over the trailing 5 minutes; measures feed ingestion, not external event frequency.';

/** Round to an integer at ≥10/min (the saturated floor); 1 decimal below. */
function formatRate(perMin: number): string {
  return perMin >= 10 ? String(Math.round(perMin)) : perMin.toFixed(1);
}

function LiveRate({ readout }: LiveRateProps) {
  // Hidden state — health query errored or unresolved. Render nothing (NFR-4).
  if (readout.kind === 'hidden') return null;

  const sourcesText = `${readout.online}/${readout.total} sources live`;
  const rateText =
    readout.kind === 'full'
      ? `${readout.isFloor ? '≥' : ''}${formatRate(readout.perMin)}/min collected`
      : null;

  const ariaLabel =
    readout.kind === 'full'
      ? `${readout.isFloor ? 'At least ' : ''}${formatRate(readout.perMin)} signals per minute collected over the trailing 5 minutes; ${readout.online} of ${readout.total} sources live`
      : `${readout.online} of ${readout.total} sources live`;

  return (
    <div
      aria-label={ariaLabel}
      title={LIVE_RATE_TITLE}
      style={{
        marginLeft: 'auto',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        fontFamily: 'var(--cd-font-mono)',
        fontSize: 10,
        color: 'var(--cd-fg-muted)',
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          background: 'var(--cd-sig-mint)',
          boxShadow: '0 0 6px var(--cd-sig-mint)',
        }}
      />
      {rateText && (
        <>
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>{rateText}</span>
          <span aria-hidden="true" style={{ opacity: 0.5 }}>
            ·
          </span>
        </>
      )}
      <span style={{ fontVariantNumeric: 'tabular-nums' }}>{sourcesText}</span>
    </div>
  );
}
